import styled from '@emotion/styled';
import { Input, Select, Tooltip } from 'antd';
import DownSvg from 'assets/svg/Down';
import { colorConstants } from 'config/colorConstants';
import { useContext, useEffect, useMemo, useState } from 'react';
import DatePickerWithEpoch from 'components/atoms/EpochDatePicker';
import tw from 'twin.macro';
import { PromoType, PromoTactic, TacticType, OfferMethod, ProductPPG } from 'types/tradePromoOptimization';
import { TPODataContext } from 'contexts/TPOContext';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { tradePromoOptimizationUrls } from 'config/urls';
import _ from 'lodash';
import { MetricsApiTriggerPoints } from '../Promos';
import RangeInputCustom from 'components/molecules/common/RangeInput';
import OfferDropdown from '../../OfferDropdown';
import { useFetchOfferData } from 'hooks/tradePromoOptimization/useFetchOfferData';
import { getConfig, sendNotification } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import { ConditionRule, FieldCondition, FieldTypes, FormField, OperatorTypes } from 'types/tpo/promoInfo';
import { PromoInfoFormFieldNames } from 'constants/tpo';
import { formFields } from 'config/tpoPromoInfoForm';
import { DownOutlined, UpOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ConfigKeys, PromotionTypes, tpoPromoMetricKeys } from 'utils/Enums';
import ErrorPopover from '../../ErrorPopOver';
import { queryClient } from 'services/reactQueryService';
import { getPromoMetricByNameKeyName, isPromoYearChangeNotAllowed } from '../../tpoHelperMethods';
import { hasInvalidChars } from 'utils/inputHelper';
import { IconTextPopover, PopoverItem } from 'components/atoms/IconTextPopover/IconTextPopover';

// todo: need to move all the dropdown values to db to make it configurable
const offerMethodDropdownOptions: { label: string; value: OfferMethod }[] = [
    // { label: 'Event library', value: 'event_library' },
    { label: 'Manual', value: 'manual' },
    // { label: 'Previous', value: 'previous' },
];

export const manualOfferDropdownOptions: { label: string; value: string }[] = [
    { label: 'Discount', value: 'price_off' },
    { label: 'Combination Offer', value: 'combination_offer' },
    { label: 'Single', value: 'single' },
    { label: 'Buy For', value: 'buy_for' },

    // { label: '2_FOR', value: '2_for' },
    // { label: '3_FOR', value: '3_for' },
];

const featureQualityDropdownOptions: { label: string; value: string }[] = [
    { label: 'Feature A', value: 'feature_a' },
    { label: 'Feature B', value: 'feature_b' },
    { label: 'Feature C', value: 'feature_c' },
];

const displayQualityDropdownOptions: { label: string; value: string }[] = [
    { label: 'Display A', value: 'display_a' },
    { label: 'Display B', value: 'display_b' },
    { label: 'Display C', value: 'display_c' },
];

const promoStatusDropdownOptions: { label: string; value: string; disabled: boolean }[] = [
    { label: 'Draft', value: 'draft', disabled: false },
    { label: 'Planned', value: 'planned', disabled: false },
    { label: 'Submitted for Approval', value: 'submitted_for_approval', disabled: true },
    { label: 'Pending Approval', value: 'pending_approval', disabled: true },
    { label: 'Rejected', value: 'rejected', disabled: true },
    { label: 'Approved', value: 'approved', disabled: true },
    { label: 'Customer Accepted', value: 'customer_accepted', disabled: true },
    { label: 'Cancelled', value: 'cancelled', disabled: true },
    { label: 'Executed', value: 'executed', disabled: true },
];

const promoFundingReqDropdownOptions: { label: string; value: string; disabled: boolean }[] = [
    { label: 'Meet Comp Funds', value: 'meet_comp_funds', disabled: false },
    { label: 'Incremental Volume', value: 'incremental_volume', disabled: false },
    { label: 'National Overlay', value: 'national_overlay', disabled: false },
];

const CustomSelect = styled(Select)`
    ${tw`border !border-borderGreyAl1 !rounded-md text-[13px] text-headerColor`}
    .ant-select-selector {
        height: 100% !important;
        display: flex;
        align-items: center;
        box-shadow: none !important;
        border: none !important;
    }
    .ant-select-arrow {
        ${tw`text-headerColor`}
    }
`;
interface IProps {
    setFetchMetricsTriggeredFrom: React.Dispatch<React.SetStateAction<MetricsApiTriggerPoints>>;
    fetchMetricsTriggeredFrom: MetricsApiTriggerPoints;
    mechanicType: string;
    eventId: null | string;
}

const PromoInfoSection: React.FC<IProps> = ({
    fetchMetricsTriggeredFrom,
    setFetchMetricsTriggeredFrom,
    mechanicType,
    eventId,
}) => {
    const {
        selectedPlanInfo,
        selectedEventInfo,
        tempEventProperties,
        updateSelectedEventProperty,
        promoConfig,
        onGetMetricRecomValuesSuccess,
        isValid,
        setIsValid,
        viewMode,
        isOptimizationInProgress,
        isCurrentPromoFormDisabled,
        isTpmTab,
        promoPopupErrors,
        selectedPromoPPGIds,
        upliftModelMutation,
        promoActiveTab,
        dimensionConfigData,
        promoConfigDictionary,
    } = useContext(TPODataContext);
    const { appConfig } = useContext(UserContext);
    const tradePromoOptimizationConfig = getConfig('tradePromoOptimization', appConfig);
    const { dateFormat } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);
    const {
        units: { currency },
    } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);

    const keysForRecommendedMetrics = ['promoType', 'tacticType', 'promoStartDate', 'promoEndDate', 'ppgs'];

    const [triggerOfferApi, setTriggerOfferApi] = useState(false);
    const [promoInfoFormFields, setPromoInfoFormFields] = useState(formFields);
    const [isExpanded, setIsExpanded] = useState<boolean>(true);

    const variablesForRecommendedMetrics = useMemo(() => {
        const variables = Object.fromEntries(
            keysForRecommendedMetrics.map((key) => {
                switch (key) {
                    case 'promoStartDate':
                        return ['start_date', selectedEventInfo?.currentPromoTactic?.[key]];
                    case 'promoEndDate':
                        return ['end_date', selectedEventInfo?.currentPromoTactic?.[key]];
                    // if ppgs change is triggering the api send only the newly added ones
                    // else send the current selected ones
                    case 'ppgs':
                        return [
                            'ppgs',
                            fetchMetricsTriggeredFrom === MetricsApiTriggerPoints.PPG
                                ? selectedEventInfo?.currentPromoTactic?.newAddedPPgs
                                : selectedPromoPPGIds,
                        ];
                    default:
                        return [_.snakeCase(key), selectedEventInfo?.currentPromoTactic?.[key], selectedPromoPPGIds];
                }
            }),
        );

        return {
            ...variables,
            dim2_attribute_name: selectedPlanInfo?.dim2Filter?.value[0],
            plan_id: selectedPlanInfo?.planId,
            event_id: eventId,
            is_tpm_plan: selectedPlanInfo?.isTpmPlan,
        };
    }, [selectedEventInfo?.currentPromoTactic, fetchMetricsTriggeredFrom]);

    useEffect(() => {
        setPromoInfoFormFields((prev) => {
            let isFormValid = true;
            const newFields = prev.map((item) => {
                const fieldProperties: any = getFieldProperties(item.conditions);
                // validation for required fields
                if (isFormValid && item.isRequired && !(fieldProperties?.isHidden || fieldProperties?.isDisabled)) {
                    isFormValid =
                        item.type === FieldTypes.Range
                            ? !!(
                                  selectedEventInfo?.currentPromoTactic?.[item.name]?.[0] &&
                                  selectedEventInfo?.currentPromoTactic?.[item.name]?.[1]
                              )
                            : !!selectedEventInfo?.currentPromoTactic?.[item.name];
                }
                if (fieldProperties)
                    return {
                        ...item,
                        isDisabled: !!fieldProperties.isDisabled,
                        isHidden: !!fieldProperties.isHidden,
                    };
                return item;
            });
            setIsValid(isFormValid);

            return newFields;
        });
    }, [selectedEventInfo?.currentPromoTactic]);

    useEffect(() => {
        if (!isValid && tempEventProperties && tempEventProperties.selectedAttributes) {
            let newName = tempEventProperties?.selectedAttributes
                ?.map((item: string) =>
                    item
                        .split(' ')
                        .slice(0, 3)
                        .map((word: string) => word[0])
                        .join(''),
                )
                .join('_');
            if (selectedEventInfo?.currentPromoTactic?.promoStartDate)
                newName += `_${dayjs(selectedEventInfo.currentPromoTactic.promoStartDate * 1000).format('MMM_DD')}`;
            if (selectedEventInfo?.currentPromoTactic)
                updateSelectedEventProperty('currentPromoTactic', {
                    ...selectedEventInfo?.currentPromoTactic,
                    name: newName,
                });
        }
    }, [
        selectedEventInfo?.currentPromoTactic?.promoStartDate,
        selectedEventInfo?.currentPromoTactic?.promoEndDate, // promo end date is required to trigger the effect so it will update the name to include updated start date
        isValid,
        tempEventProperties,
    ]);

    const { loading: metricsRecommValuesLoading } = useCommonApiHandler({
        method: 'post',
        queryKey: ['get_metrics_recommended_values', variablesForRecommendedMetrics],
        queryConfig: {
            enabled: isValid && !!fetchMetricsTriggeredFrom && variablesForRecommendedMetrics?.ppgs?.length > 0,
        },
        url: `${tradePromoOptimizationUrls.getMetricsRecommendedValues}`,
        variables: variablesForRecommendedMetrics,
        parserFunction: async (res: any) => {
            try {
                const ppgs = onGetMetricRecomValuesSuccess(res.ppgs, fetchMetricsTriggeredFrom);
                if (
                    fetchMetricsTriggeredFrom === MetricsApiTriggerPoints.PPG &&
                    [PromotionTypes.Merchandising, PromotionTypes.SM_MDF].includes(
                        Number(selectedEventInfo?.currentPromoTactic?.promoType),
                    )
                ) {
                    const edlp_metric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.EDLP_SRP);
                    const upliftPPgs = ppgs.filter((ppg) =>
                        ppg.metrics.some((metric) => metric.name === edlp_metric.name && metric.value),
                    );
                    if (upliftPPgs?.length > 0) {
                        await upliftModelMutation?.mutateAsync({ mechanicType, ppgs: upliftPPgs });
                    }
                }
                setFetchMetricsTriggeredFrom(MetricsApiTriggerPoints.None);
                return res;
            } catch (error) {
                throw error;
            }
        },
    });

    const isStatusDropdownIsDisabled = (keyName: string): boolean => {
        if (keyName === PromoInfoFormFieldNames.status) {
            const tactic = selectedEventInfo?.currentPromoTactic;
            if (!tactic?.ppgs?.length) return false;

            const allowedStatuses = ['draft', 'planned'];
            const status = tactic.status?.toLowerCase() ?? '';

            const isEditableStatus = allowedStatuses.includes(status);

            return !isEditableStatus;
        }
        return false;
    };

    const offerVariables = useMemo(() => {
        if (!triggerOfferApi) {
            return {};
        } else if (selectedEventInfo?.currentPromoTactic?.offerType) {
            return {
                selected_attributes:
                    tempEventProperties?.selectedDim1NestedAttributes?.map((item: any) => item?.id) ?? [],
                offer_type: selectedEventInfo?.currentPromoTactic.offerType,
                limit: tradePromoOptimizationConfig?.offerDataDropdownLimit,
                offset: 0,
            };
        }
    }, [triggerOfferApi, selectedEventInfo?.currentPromoTactic?.offerType]);

    //    trigger offer api, when offer method is changing
    const { loading: offerLoading, data: offerList } = useFetchOfferData({
        method: 'post',
        queryKey: ['tpo-fetch-offer-data', offerVariables],
        url: tradePromoOptimizationUrls.getOfferDataUrl,
        variables: offerVariables,
        queryConfig: {
            enabled: !_.isEmpty(offerVariables) && triggerOfferApi,
        },
    });

    useEffect(() => {
        if (selectedEventInfo?.currentPromoTactic)
            updateSelectedEventProperty('currentPromoTactic', {
                ...selectedEventInfo?.currentPromoTactic,
                isDataLoading: metricsRecommValuesLoading,
            });
    }, [metricsRecommValuesLoading]);

    const promoTypes = useMemo(() => {
        return promoConfig?.map((pr: PromoType) => ({
            value: pr.promotion_id,
            label: pr.promotion_name,
            disabled: [PromotionTypes.Write_Off, PromotionTypes.Post_Audit].includes(pr.promotion_id),
        }));
    }, [promoConfig]);

    const tacticTypes = useMemo(() => {
        if (!selectedEventInfo?.currentPromoTactic?.promoType) return [];

        return promoConfig
            ?.find((pr: PromoType) => pr.promotion_id === selectedEventInfo?.currentPromoTactic?.promoType)
            ?.tactics.map((tac: TacticType) => ({ value: tac.tactic_id, label: tac.tactic_name }));
    }, [promoConfig, selectedEventInfo?.currentPromoTactic?.promoType]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const blockedKeys = [
            '!',
            '@',
            '#',
            '^',
            '&',
            '*',
            '_',
            '+',
            '{',
            '}',
            '[',
            ']',
            ':',
            ';',
            "'",
            '<',
            '>',
            ',',
            '?',
            '|',
            '\\',
            '~',
            '`',
        ];
        if (hasInvalidChars(e.key, blockedKeys)) {
            sendNotification('error', `Invalid key pressed: "${e.key}"`);
            e.preventDefault();
        }
    };

    const updatePromoDetailsProperty = (values: Partial<PromoTactic>[]) => {
        if (selectedEventInfo) {
            const updatedPromoTactic = values.reduce(
                (acc: PromoTactic, item: Partial<PromoTactic>) => ({
                    ...acc,
                    ...item,
                }),
                { ...selectedEventInfo.currentPromoTactic, hasChanges: true },
            );
            updateSelectedEventProperty('currentPromoTactic', updatedPromoTactic);
        }
    };

    const renderLabelWithRequiredCondition = (field: FormField) => {
        if (!field) return null;

        const labelContent = (
            <>
                {field.isRequired && <span className="text-redTriangle mr-1">*</span>}
                {field.label}:
            </>
        );

        // Conditionally wrap the label with Tooltip if tooltip exists and is not empty
        return field.tooltip ? (
            <Tooltip title={field.tooltip}>
                <span>{labelContent}</span>
            </Tooltip>
        ) : (
            <span>{labelContent}</span>
        );
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, label: string, maxCharLimit?: number) => {
        if (maxCharLimit && e.target.value.length > maxCharLimit) {
            sendNotification('error', `${label} Character limit exceeded. Max ${maxCharLimit} characters allowed.`);
        } else {
            updatePromoDetailsProperty([{ [e.target.name]: e.target.value }]);
        }
    };

    const onChangeTacticType = (val: any, item: any) => {
        updatePromoDetailsProperty([{ tacticType: item?.value }, { featureQuality: null }, { displayQuality: null }]);
        setFetchMetricsTriggeredFrom(MetricsApiTriggerPoints.Tactic);
    };

    const onChangePromoType = (val: any, item: any) => {
        updatePromoDetailsProperty([
            { promoType: item?.value },
            { tacticType: null },
            { featureQuality: null },
            { displayQuality: null },
            { offer: null },
            { offerType: null },
            { manualOffer: null },
            { buyGet: null },
            { buyFor: null },
            { discount: null },
            { parNumber: null },
            { ismCampaignId: null },
        ]);
    };

    const onChangePromoStatusType = (val: any, item: any) => {
        updatePromoDetailsProperty([{ status: item?.value }]);
    };

    const onChangeFundingReq = (val: any, item: any) => {
        updatePromoDetailsProperty([{ fundingRequest: item?.value }]);
    };

    const onChangeOfferType = (val: any, item: any) => {
        updatePromoDetailsProperty([
            { offerType: item?.value },
            { manualOffer: null },
            { offer: null },
            { buyGet: null },
            { buyFor: null },
            { discount: null },
        ]);
    };

    const onChangeManualOffer = (val: any, item: any) => {
        updatePromoDetailsProperty([
            { manualOffer: item?.value },
            { offer: null },
            { buyGet: null },
            { buyFor: null },
            { discount: null },
        ]);
    };

    const onChangeDate = (date: number | null, key: string, copyValueTo?: string) => {
        const oldDate = selectedEventInfo?.currentPromoTactic[key];
        const newDate = date;

        if (isPromoYearChangeNotAllowed(oldDate, newDate, selectedEventInfo?.currentPromoTactic.status || '')) {
            sendNotification('error', 'Year cannot be changed once the promo is approved.');
            return false;
        }

        const updatedArray: any[] = [{ [key]: date }];

        // If promoStartDate is changed, clear promoEndDate
        if (key === PromoInfoFormFieldNames.PromoStartDate) {
            updatedArray.push({ [PromoInfoFormFieldNames.PromoEndDate]: null });
        }

        // Handle CRA dates copy logic
        if (copyValueTo && date) {
            const planYear = selectedPlanInfo?.planYear;
            if (planYear) {
                const yearStart = dayjs().set('year', planYear).startOf('year').unix();
                const yearEnd = dayjs().set('year', planYear).endOf('year').unix();

                if (date >= yearStart && date <= yearEnd) {
                    updatedArray.push({ [copyValueTo]: date });
                } else {
                    console.log(`Not copying to ${copyValueTo} as date is out of plan year`);
                }
            }
        }

        updatePromoDetailsProperty(updatedArray);

        if (date) {
            setFetchMetricsTriggeredFrom(MetricsApiTriggerPoints.Date);
        }
    };

    const getDropDownOptionsAndCustomOnChange = (fieldName: string) => {
        switch (fieldName) {
            case PromoInfoFormFieldNames.PromoType:
                return { options: promoTypes, onChange: onChangePromoType };
            case PromoInfoFormFieldNames.TacticType:
                return { options: tacticTypes, onChange: onChangeTacticType };
            case PromoInfoFormFieldNames.OfferMethod:
                return {
                    options: offerMethodDropdownOptions,
                    onChange: onChangeOfferType,
                };
            case PromoInfoFormFieldNames.ManualOffer:
                return {
                    options: manualOfferDropdownOptions,
                    onChange: onChangeManualOffer,
                };
            case PromoInfoFormFieldNames.DisplayQuality:
                return { options: displayQualityDropdownOptions };
            case PromoInfoFormFieldNames.FeatureQuality:
                return { options: featureQualityDropdownOptions };
            case PromoInfoFormFieldNames.status:
                return { options: promoStatusDropdownOptions, onChange: onChangePromoStatusType };
            case PromoInfoFormFieldNames.FundingRequest:
                return { options: promoFundingReqDropdownOptions, onChange: onChangeFundingReq };
            default:
                return { options: [] };
        }
    };

    const getCustomOnChangeDate = (fieldName: string) => {
        switch (fieldName) {
            case PromoInfoFormFieldNames.PromoStartDate:
            case PromoInfoFormFieldNames.PromoEndDate:
                return {
                    onChange: onChangeDate,
                };
            case PromoInfoFormFieldNames.CRAStartDate: {
                return {
                    onChange: (date: number | null, key: string) => {
                        const updationArr = [{ [key]: date, [PromoInfoFormFieldNames.CRAEndDate]: null }];
                        if (Number(date) > Number(selectedEventInfo?.currentPromoTactic?.promoStartDate))
                            updationArr.push({
                                [PromoInfoFormFieldNames.PromoStartDate]: date,
                                [PromoInfoFormFieldNames.PromoEndDate]: null,
                            });
                        updatePromoDetailsProperty(updationArr);
                    },
                };
            }
            default:
                return { onChange: null };
        }
    };

    const combinedViewMode = isOptimizationInProgress || viewMode || isTpmTab || isCurrentPromoFormDisabled;

    const readConditions = (conditions: ConditionRule[], conjunction: 'all' | 'any') => {
        switch (conjunction) {
            case 'all': {
                return conditions.every((item) => {
                    const { field, operator, value } = item;
                    return compareValues(value, selectedEventInfo?.currentPromoTactic?.[field], operator);
                });
            }
            case 'any': {
                return conditions.some((item) => {
                    const { operator, value, field } = item;
                    return compareValues(value, selectedEventInfo?.currentPromoTactic?.[field], operator);
                });
            }
            default:
                return false;
        }
    };

    const compareValues = (targetValue: any, value: any, operator: OperatorTypes) => {
        switch (operator) {
            case OperatorTypes.isEqual:
                return value === targetValue;
            case OperatorTypes.isNotEqual:
                return value !== targetValue;

            case OperatorTypes.isEmpty:
                return !value;
            case OperatorTypes.isNotEmpty:
                return !!value;
            case OperatorTypes.In:
                return targetValue.includes(value);
            case OperatorTypes.NotIn:
                return !targetValue.includes(value);
            default:
                return false;
        }
    };

    const getFieldProperties = (conditions: FieldCondition | undefined) => {
        if (!conditions) return false;
        const { rules, action } = conditions;
        const shouldHide = rules.some((rule) => {
            const { conjunction, conditions } = rule;
            if (readConditions(conditions, conjunction)) {
                return true;
            }
        });

        return {
            [action]: shouldHide,
        };
    };

    const getEnabledDates = (formField: string) => {
        const planYear = selectedPlanInfo?.planYear;
        if (!planYear) return {};

        const today = dayjs().unix();

        switch (formField) {
            case 'promoStartDate': {
                const craStartDate = selectedEventInfo?.currentPromoTactic?.craStartDate;
                const planStart = dayjs().set('year', planYear).startOf('year').unix(); // Jan 1 of plan year
                const allowedStartDate = Math.max(Number(craStartDate ?? 0), planStart, today);

                return {
                    start: allowedStartDate,
                    end: Math.max(
                        dayjs()
                            .set('year', planYear + 1)
                            .endOf('year')
                            .unix(), // Dec 31, planYear+1
                        today,
                    ),
                };
            }

            case 'promoEndDate': {
                const promoStart = selectedEventInfo?.currentPromoTactic?.promoStartDate ?? 0;
                const fallbackStart = dayjs().set('year', planYear).startOf('year').unix();
                const allowedStart = Math.max(promoStart, fallbackStart, today);

                return {
                    start: allowedStart,
                    end: Math.max(
                        dayjs()
                            .set('year', planYear + 1)
                            .endOf('year')
                            .unix(), // Dec 31, planYear+1
                        today,
                    ),
                };
            }

            case 'craStartDate': {
                const start = Math.max(dayjs().set('year', planYear).startOf('year').unix(), today);

                return {
                    start: start,
                    end: Math.max(
                        dayjs().set('year', planYear).endOf('year').unix(), // Dec 31, planYear
                        today,
                    ),
                };
            }

            case 'craEndDate': {
                const craStart = selectedEventInfo?.currentPromoTactic?.craStartDate ?? 0;
                const fallbackStart = dayjs().set('year', planYear).startOf('year').unix();
                const allowedStart = Math.max(craStart, fallbackStart, today);

                return {
                    start: allowedStart,
                    end: Math.max(
                        dayjs().set('year', planYear).endOf('year').unix(), // Dec 31, planYear
                        today,
                    ),
                };
            }

            default:
                return {};
        }
    };
    const renderFormField = (field: FormField, isFieldDisabled: boolean) => {
        const { name, placeholder, copyValueTo, maxCharLimit, label, disableUnits } = field;
        switch (field.type) {
            case FieldTypes.Text:
                return (
                    <Input
                        className="!w-full !rounded-md"
                        value={selectedEventInfo?.currentPromoTactic?.[name]}
                        name={name}
                        onChange={(e) => handleInputChange(e, label, maxCharLimit)}
                        disabled={
                            combinedViewMode ||
                            isFieldDisabled ||
                            (name === PromoInfoFormFieldNames.PromoName && !isValid)
                        }
                        onKeyDown={handleKeyDown}
                    />
                );
            case FieldTypes.Select: {
                const { options, onChange } = getDropDownOptionsAndCustomOnChange(name);
                return (
                    <CustomSelect
                        suffixIcon={<DownSvg dropDownStroke={colorConstants.greyAlt17} />}
                        className="dropdown-height !w-full overflow-hidden"
                        value={selectedEventInfo?.currentPromoTactic?.[name]}
                        options={options}
                        onChange={
                            onChange ?? ((val, option: any) => updatePromoDetailsProperty([{ [name]: option?.value }]))
                        }
                        disabled={combinedViewMode || isFieldDisabled || isStatusDropdownIsDisabled(name)}
                    />
                );
            }
            case FieldTypes.Date: {
                const { onChange } = getCustomOnChangeDate(name);
                const enabledDates = getEnabledDates(name);
                return (
                    <DatePickerWithEpoch
                        value={selectedEventInfo?.currentPromoTactic?.[name] || 0} // Pass the epoch timestamp
                        onChange={(date: number | null) => {
                            if (onChange) {
                                onChange(date, name, copyValueTo);
                            } else {
                                const updationArr = [{ [name]: date }];
                                if (copyValueTo) {
                                    updationArr.push({ [copyValueTo]: date });
                                }
                                updatePromoDetailsProperty(updationArr);
                            }
                        }}
                        format="MM/DD/YYYY" // Specify date format
                        placeholder={placeholder}
                        showToday={true}
                        classNames="!w-full !rounded-md"
                        enabledDates={enabledDates}
                        disabled={combinedViewMode || isFieldDisabled}
                    />
                );
            }
            case FieldTypes.PriceOff:
                return (
                    <div className="relative w-full">
                        <Input
                            type="number"
                            onChange={(e) => updatePromoDetailsProperty([{ [name]: Number(e.target.value) }])}
                            disabled={combinedViewMode || isFieldDisabled}
                            placeholder={placeholder}
                            className="!w-full !rounded-md"
                            value={selectedEventInfo?.currentPromoTactic?.[name]}
                            suffix={
                                <div className="flex space-x-2">
                                    <span
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            updatePromoDetailsProperty([{ discountUnit: 'abs' }]);
                                        }}
                                        className={`mr-2 cursor-pointer ${
                                            selectedEventInfo?.currentPromoTactic?.discountUnit === 'abs'
                                                ? 'text-purpleFont font-bold'
                                                : 'text-promoColour'
                                        }`}
                                    >
                                        Abs
                                    </span>
                                    <span
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            updatePromoDetailsProperty([{ discountUnit: '%' }]);
                                        }}
                                        className={`cursor-pointer ${
                                            selectedEventInfo?.currentPromoTactic?.discountUnit === '%'
                                                ? 'text-purpleFont font-bold'
                                                : 'text-promoColour'
                                        }`}
                                    >
                                        %
                                    </span>
                                </div>
                            }
                        />
                    </div>
                );
            case FieldTypes.Range:
                return (
                    <div className="w-full flex items-center">
                        <RangeInputCustom
                            maxVal={
                                selectedEventInfo?.currentPromoTactic?.[name]?.length
                                    ? selectedEventInfo.currentPromoTactic?.[name]?.[1]
                                    : 0
                            }
                            minVal={
                                selectedEventInfo?.currentPromoTactic?.[name]?.length
                                    ? selectedEventInfo.currentPromoTactic?.[name]?.[0]
                                    : 0
                            }
                            onMaxChange={(val) =>
                                updatePromoDetailsProperty([
                                    {
                                        [name]: Array.isArray(selectedEventInfo?.currentPromoTactic?.[name])
                                            ? [selectedEventInfo?.currentPromoTactic?.[name]?.[0] || 0, val]
                                            : [0, val],
                                    },
                                ])
                            }
                            onMinChange={(val) => {
                                updatePromoDetailsProperty([
                                    {
                                        [name]: Array.isArray(selectedEventInfo?.currentPromoTactic?.[name])
                                            ? [val, selectedEventInfo?.currentPromoTactic?.[name]?.[1] || 0]
                                            : [val, 0],
                                    },
                                ]);
                            }}
                            inputClassName="max-w-[80px]"
                            placeHolder={[undefined, undefined]}
                        />
                        {!disableUnits && (
                            <div className="flex space-x-0.5">
                                <span
                                    onClick={() => {
                                        updatePromoDetailsProperty([{ comboUnit: 'abs' }]);
                                    }}
                                    className={`ml-1 cursor-pointer ${
                                        selectedEventInfo?.currentPromoTactic?.comboUnit === 'abs'
                                            ? 'text-purpleFont font-bold'
                                            : 'text-promoColour'
                                    }`}
                                >
                                    Abs
                                </span>
                                <span
                                    onClick={() => {
                                        updatePromoDetailsProperty([{ comboUnit: '%' }]);
                                    }}
                                    className={`cursor-pointer ${
                                        selectedEventInfo?.currentPromoTactic?.comboUnit === '%'
                                            ? 'text-purpleFont font-bold'
                                            : 'text-promoColour'
                                    }`}
                                >
                                    %
                                </span>
                                <span
                                    onClick={() => {
                                        updatePromoDetailsProperty([{ comboUnit: 'item' }]);
                                    }}
                                    className={`cursor-pointer ${
                                        selectedEventInfo?.currentPromoTactic?.comboUnit === 'item'
                                            ? 'text-purpleFont font-bold'
                                            : 'text-promoColour'
                                    }`}
                                >
                                    Item
                                </span>
                            </div>
                        )}
                    </div>
                );
            case FieldTypes.OfferLibrary:
                return (
                    <OfferDropdown
                        disabled={isFieldDisabled}
                        onDropdownVisibleChange={(open) => {
                            if (open) setTriggerOfferApi(true);
                            else setTriggerOfferApi(false);
                        }}
                        data={offerList}
                        placeholder={placeholder}
                        className="dropdown-height !w-full !rounded-md offer-dropdown"
                        loading={offerLoading}
                        onChange={(val, option: any) => updatePromoDetailsProperty([{ [name]: option?.value }])}
                        value={selectedEventInfo?.currentPromoTactic?.[name]}
                    />
                );
            default:
                return null;
        }
    };

    const handleRefresh = () => {
        const currentPromoTactic = selectedEventInfo?.currentPromoTactic;
        if (currentPromoTactic) {
            updateSelectedEventProperty('currentPromoTactic', { ...currentPromoTactic, isLoading: true });
            queryClient.refetchQueries({
                queryKey: ['tpo_get_promo_details_by_id'],
                exact: false, // useful if the query key has dynamic parts
            });
        }
    };

    const popOverItems = useMemo(() => {
        const hoverItems: PopoverItem[] = [];
        if (dimensionConfigData?.dim1Attribute?.label) {
            hoverItems.push({
                title: dimensionConfigData?.dim1Attribute?.label,
                content: selectedPlanInfo?.dim1Filter?.value?.join(', ') || '',
            });
        }
        if (dimensionConfigData?.dim2Attribute?.label) {
            hoverItems.push({
                title: dimensionConfigData?.dim2Attribute?.label,
                content: selectedPlanInfo?.dim2Filter?.value?.join(', ') || '',
            });
        }
        // todo : need to add Customer division details
        if (true) {
            hoverItems.push({ title: 'Customer Division', content: '-' });
        }

        hoverItems.push({
            title: 'TPO Promo ID',
            content: promoActiveTab?.toString() === '0' ? '-' : promoActiveTab?.toString() || '',
        });

        if (tempEventProperties?.name) {
            hoverItems.push({ title: 'Parent Event Name', content: tempEventProperties.name });
        }
        return hoverItems;
    }, [tempEventProperties]);

    return (
        <div className="bg-backgroundGreyAlt6 p-3 rounded">
            <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex gap-2 items-center">
                    <span className="font-bold">Add Promo Details</span>
                    <IconTextPopover icon={InfoCircleOutlined} text="Promo Details" items={popOverItems} />
                    {promoPopupErrors.length > 0 && (
                        <div className="mx-3 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <InfoCircleOutlined style={{ color: colorConstants.redAlt }} />
                            <ErrorPopover promoPopupErrors={promoPopupErrors} />
                        </div>
                    )}
                    {selectedEventInfo?.currentPromoTactic?.isRefreshRequired && (
                        <button onClick={handleRefresh}> refresh</button>
                    )}
                </div>
                {isExpanded ? (
                    <UpOutlined className="!text-purpleFont" />
                ) : (
                    <DownOutlined className="!text-purpleFont" />
                )}
            </div>
            {isExpanded && (
                <>
                    <div className="flex flex-wrap gap-x-4 mt-3 items-center text-black54">
                        <div>Below LUC: {selectedEventInfo?.currentPromoTactic?.belowLUC}</div>
                        <div>Below MPIC: {selectedEventInfo?.currentPromoTactic?.belowMPIC}</div>
                        <div>
                            Event Date:
                            {tempEventProperties.CRAStartDate && tempEventProperties.CRAEndDate
                                ? ` ${tempEventProperties.CRAStartDate} - ${tempEventProperties.CRAEndDate}`
                                : ' -'}
                        </div>
                        <div>
                            Approval Date:
                            {selectedEventInfo?.currentPromoTactic?.approvedDate
                                ? ` ${dayjs(selectedEventInfo?.currentPromoTactic?.approvedDate * 1000)
                                      .startOf('day')
                                      .format(dateFormat)}`
                                : ' -'}
                        </div>
                        <div>Mechanic: {mechanicType}</div>
                    </div>
                    {/* PRODUCT INFO FOORM */}
                    <div className="grid grid-rows-5 grid-flow-col grid-cols-[280px_300px_280px_300px] gap-y-4 xl:gap-x-4 2xl:gap-x-16 w-full py-3 justify-center items-center">
                        {promoInfoFormFields.map((field) => {
                            // const fieldProperties: any = getFieldProperties(field.conditions);
                            const { isDisabled, isHidden } = field;
                            if (isHidden) return null;
                            return (
                                <div key={field.name} className="flex gap-4 w-full items-center">
                                    <div className="basis-5/12 text-right flex gap-1 justify-end items-center ">
                                        {renderLabelWithRequiredCondition(field)}
                                    </div>
                                    <div className="basis-7/12 min-w-0">{renderFormField(field, !!isDisabled)}</div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

export default PromoInfoSection;
